import React, { useState, useMemo } from 'react';
import * as LucideIcons from 'lucide-react';
import { Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';

// Curated categories for better organization
const ICON_CATEGORIES = {
  'Common': ['Home', 'User', 'Settings', 'Search', 'Star', 'Heart', 'Mail', 'Phone', 'Calendar', 'Clock', 'Check', 'X', 'Plus', 'Minus', 'Edit', 'Trash', 'Download', 'Upload', 'Share', 'Link'],
  'Business': ['Building', 'Building2', 'Briefcase', 'Wallet', 'CreditCard', 'DollarSign', 'PiggyBank', 'Receipt', 'FileText', 'ClipboardList', 'Target', 'TrendingUp', 'BarChart', 'PieChart', 'LineChart'],
  'Real Estate': ['Home', 'Building', 'Building2', 'MapPin', 'Map', 'Key', 'DoorOpen', 'Bed', 'Bath', 'Car', 'Trees', 'Mountain', 'Landmark', 'Store', 'Warehouse'],
  'People': ['User', 'Users', 'UserPlus', 'UserCheck', 'Contact', 'Handshake', 'Heart', 'HeartHandshake', 'Baby', 'PersonStanding', 'Accessibility'],
  'Events': ['Calendar', 'CalendarDays', 'CalendarCheck', 'Clock', 'Alarm', 'Timer', 'PartyPopper', 'Gift', 'Cake', 'Sparkles', 'Trophy', 'Medal', 'Award'],
  'Media': ['Image', 'Images', 'Camera', 'Video', 'Film', 'Music', 'Mic', 'Headphones', 'Radio', 'Tv', 'Monitor', 'Smartphone', 'Tablet'],
  'Communication': ['Mail', 'MessageCircle', 'MessageSquare', 'Phone', 'PhoneCall', 'Send', 'Bell', 'Megaphone', 'AtSign', 'Globe', 'Wifi', 'Signal'],
  'Nature': ['Sun', 'Moon', 'Cloud', 'CloudRain', 'Snowflake', 'Wind', 'Leaf', 'Trees', 'Flower', 'Mountain', 'Waves', 'Droplet', 'Flame'],
  'Transport': ['Car', 'Truck', 'Bus', 'Train', 'Plane', 'Ship', 'Bike', 'Rocket', 'Navigation', 'MapPin', 'Route', 'Compass'],
  'Food & Drink': ['Coffee', 'UtensilsCrossed', 'Pizza', 'Apple', 'Beef', 'Cake', 'Cookie', 'Croissant', 'IceCream', 'Wine', 'Beer', 'CupSoda'],
  'Tech': ['Laptop', 'Monitor', 'Smartphone', 'Tablet', 'Cpu', 'HardDrive', 'Database', 'Server', 'Code', 'Terminal', 'Bug', 'Wifi', 'Bluetooth'],
  'Shapes': ['Circle', 'Square', 'Triangle', 'Hexagon', 'Pentagon', 'Octagon', 'Diamond', 'Star', 'Heart', 'Sparkle', 'Zap'],
  'Arrows': ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'ArrowUpRight', 'ArrowDownLeft', 'ChevronUp', 'ChevronDown', 'ChevronLeft', 'ChevronRight', 'ChevronsUp', 'MoveUp', 'MoveDown'],
};

// Get all available icon names (excluding non-icon exports)
const getAllIconNames = () => {
  return Object.keys(LucideIcons).filter(name => {
    // Only include actual icon components (start with uppercase)
    // Exclude names ending with 'Icon' suffix and utility functions
    return /^[A-Z]/.test(name) && 
           !name.endsWith('Icon') &&
           name !== 'createLucideIcon' &&
           name !== 'default' &&
           name !== 'icons' &&
           LucideIcons[name] !== undefined;
  });
};

const ALL_ICONS = getAllIconNames();

/**
 * Icon Picker Component
 * Allows users to search and select from all lucide-react icons
 */
export function IconPicker({ 
  value, 
  onChange, 
  label = 'Icon',
  placeholder = 'Select an icon',
  testId = 'icon-picker'
}) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('Common');

  // Get the currently selected icon component
  const SelectedIcon = value ? LucideIcons[value] : null;

  // Filter icons based on search
  const filteredIcons = useMemo(() => {
    if (!searchQuery.trim()) {
      // Return icons from active category
      const categoryIcons = ICON_CATEGORIES[activeCategory] || [];
      return categoryIcons.filter(name => LucideIcons[name]);
    }
    
    // Search across all icons
    const query = searchQuery.toLowerCase();
    return ALL_ICONS.filter(name => 
      name.toLowerCase().includes(query)
    ).slice(0, 100); // Limit results for performance
  }, [searchQuery, activeCategory]);

  const handleSelect = (iconName) => {
    onChange(iconName);
    setOpen(false);
    setSearchQuery('');
  };

  const handleClear = () => {
    onChange(null);
  };

  return (
    <div className="space-y-2">
      {label && <Label>{label}</Label>}
      
      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          className="flex-1 justify-start"
          onClick={() => setOpen(true)}
          data-testid={`${testId}-trigger`}
        >
          {SelectedIcon ? (
            <div className="flex items-center gap-2">
              <SelectedIcon className="w-4 h-4" />
              <span>{value}</span>
            </div>
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
        </Button>
        
        {value && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={handleClear}
            data-testid={`${testId}-clear`}
          >
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Select Icon</DialogTitle>
            <DialogDescription>
              Choose an icon from the library or search for a specific one
            </DialogDescription>
          </DialogHeader>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search icons..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid={`${testId}-search`}
            />
            {searchQuery && (
              <Button
                variant="ghost"
                size="sm"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                onClick={() => setSearchQuery('')}
              >
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>

          {/* Category Tabs (only show when not searching) */}
          {!searchQuery && (
            <div className="flex flex-wrap gap-1 pb-2 border-b">
              {Object.keys(ICON_CATEGORIES).map(category => (
                <Button
                  key={category}
                  variant={activeCategory === category ? "secondary" : "ghost"}
                  size="sm"
                  className="text-xs h-7"
                  onClick={() => setActiveCategory(category)}
                >
                  {category}
                </Button>
              ))}
            </div>
          )}

          {/* Icon Grid */}
          <ScrollArea className="h-[300px]">
            {filteredIcons.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <Search className="w-8 h-8 text-muted-foreground/50 mb-2" />
                <p className="text-sm text-muted-foreground">No icons found</p>
                <p className="text-xs text-muted-foreground">Try a different search term</p>
              </div>
            ) : (
              <div className="grid grid-cols-6 sm:grid-cols-8 gap-2 p-1">
                {filteredIcons.map(iconName => {
                  const Icon = LucideIcons[iconName];
                  if (!Icon) return null;
                  
                  const isSelected = value === iconName;
                  
                  return (
                    <button
                      key={iconName}
                      type="button"
                      onClick={() => handleSelect(iconName)}
                      className={`
                        flex flex-col items-center justify-center p-3 rounded-lg transition-colors
                        hover:bg-accent
                        ${isSelected ? 'bg-primary text-primary-foreground' : 'bg-muted/50'}
                      `}
                      title={iconName}
                      data-testid={`${testId}-icon-${iconName}`}
                    >
                      <Icon className="w-5 h-5" />
                    </button>
                  );
                })}
              </div>
            )}
          </ScrollArea>

          {/* Search results count */}
          {searchQuery && (
            <p className="text-xs text-muted-foreground text-center">
              Showing {filteredIcons.length} of {ALL_ICONS.length} icons
            </p>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * Render a Lucide icon by name
 */
export function DynamicIcon({ name, className = "w-4 h-4", fallback = null }) {
  const Icon = name ? LucideIcons[name] : null;
  
  if (!Icon) {
    return fallback;
  }
  
  return <Icon className={className} />;
}

export default IconPicker;
